"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { regionFromPathname } from "@/lib/region";
import { richParagraphs } from "@/lib/richtext";
import { cn } from "@/lib/utils";
import { usePublicPathname } from "@/lib/public-pathname";

type EmergencyNumberBlok = SbBlokData & {
  label?: string;
  number?: string;
  tel?: string;
  note?: string;
};

type EmergencyContactBlok = SbBlokData & {
  heading?: string;
  description?: unknown;
  symptoms_heading?: string;
  symptoms?: string;
  contacts?: EmergencyNumberBlok[];
  footnote?: string;
  bg_color?: unknown;
  panel_color?: unknown;
};

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <path
        d="M6.3 3.5h2.9l1.5 3.7-2 1.3a11 11 0 0 0 5 5l1.3-2 3.7 1.5v2.9a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.3 5.7a2 2 0 0 1 2-2.2Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function telHref(contact: EmergencyNumberBlok): string {
  const raw = (contact.tel || contact.number || "").trim();
  return `tel:${raw.replace(/[^\d+]/g, "")}`;
}

export default function EmergencyContact({ blok }: { blok: EmergencyContactBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const surface = surfaceFrom(blok.bg_color);
  const panel = surfaceFrom(blok.panel_color);
  const on = panel ?? surface;

  const symptoms = (blok.symptoms || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  const contacts = (blok.contacts ?? []).filter((c) => (c.number || "").trim());
  const description = richParagraphs(blok.description, region);

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div
        style={panel ? { backgroundColor: panel.backgroundColor } : undefined}
        className={cn(
          "relative mx-auto w-full max-w-7xl rounded-3xl p-6 md:p-8 lg:p-12",
          panel ? "" : "bg-white",
        )}
      >
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-14">
          <div className="flex flex-col gap-5 lg:w-[520px] lg:shrink-0 lg:justify-center">
            {blok.heading && (
              <h2 className={cn("text-section", headingOn(on, "text-teal-07"))}>{blok.heading}</h2>
            )}
            {description.length > 0 && (
              <div
                className={cn(
                  "flex flex-col gap-4 text-base leading-relaxed lg:text-lg",
                  onSurface(on, "text-white/85", "text-grey-07", "text-grey-07"),
                )}
              >
                {description.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-1 flex-col gap-6">
            {symptoms.length > 0 && (
              <div className="flex flex-col gap-3">
                {blok.symptoms_heading && (
                  <p
                    className={cn(
                      "text-lg font-bold",
                      onSurface(on, "text-white", "text-grey-09", "text-grey-09"),
                    )}
                  >
                    {blok.symptoms_heading}
                  </p>
                )}
                <ul className="flex flex-wrap gap-2">
                  {symptoms.map((s) => (
                    <li
                      key={s}
                      className={cn(
                        "rounded-full px-4 py-2 text-base font-medium",
                        onSurface(
                          on,
                          "bg-white/10 text-white",
                          "bg-teal-01 text-teal-08",
                          "bg-teal-01 text-teal-08",
                        ),
                      )}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {contacts.length > 0 && (
              <ul className="grid gap-3 sm:grid-cols-2">
                {contacts.map((c) => (
                  <li key={c._uid as string}>
                    <a
                      href={telHref(c)}
                      className={cn(
                        "group flex h-full items-center gap-4 rounded-2xl border p-4 transition-colors lg:p-5",
                        onSurface(
                          on,
                          "border-white/20 hover:border-white/40",
                          "border-grey-01 hover:border-teal-04",
                          "border-grey-01 hover:border-teal-04",
                        ),
                      )}
                    >
                      <span
                        className={cn(
                          "flex size-11 shrink-0 items-center justify-center rounded-full",
                          onSurface(
                            on,
                            "bg-white/10 text-white",
                            "bg-teal-01 text-teal-08",
                            "bg-teal-01 text-teal-08",
                          ),
                        )}
                      >
                        <PhoneIcon className="size-6" />
                      </span>
                      <span className="flex min-w-0 flex-col">
                        {c.label && (
                          <span
                            className={cn(
                              "text-base leading-snug",
                              onSurface(on, "text-white/70", "text-grey-06", "text-grey-06"),
                            )}
                          >
                            {c.label}
                          </span>
                        )}
                        <span className="text-teal-08 text-xl font-bold group-hover:underline">
                          {c.number}
                        </span>
                        {c.note && (
                          <span
                            className={cn(
                              "text-sm leading-snug",
                              onSurface(on, "text-white/70", "text-grey-06", "text-grey-06"),
                            )}
                          >
                            {c.note}
                          </span>
                        )}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {blok.footnote && (
              <p
                className={cn(
                  "text-base leading-relaxed",
                  onSurface(on, "text-white/70", "text-grey-06", "text-grey-06"),
                )}
              >
                {blok.footnote}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
